import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Eye, EyeOff, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import RecordMetadata from '@/components/RecordMetadata';
import { settingCreateSchema, VALUE_TYPES, type SettingCreate, type AuditProfile } from '@prolarus/shared';

export interface SettingRecord {
  id: string;
  setting_key: string;
  setting_value: unknown;
  default_value: unknown;
  value_type: string;
  allowed_values: unknown;
  category: string;
  name: string;
  description: string | null;
  seq_num: number;
  is_system: boolean;
  is_sensitive: boolean;
  is_deletable: boolean;
  is_hidden_from_tenant: boolean;
  requires_restart: boolean;
  created?: string | null;
  modified?: string | null;
  created_by_profile?: AuditProfile | null;
  updated_by_profile?: AuditProfile | null;
}

interface SettingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setting: SettingRecord | null;
  onSave: (data: SettingCreate) => Promise<void>;
  onDelete?: (setting: SettingRecord) => void;
}

export default function SettingFormDialog({ open, onOpenChange, setting, onSave, onDelete }: SettingFormDialogProps) {
  const isEditing = !!setting;
  const isReadOnly = !!setting?.is_system;
  const canDelete = !!setting && !setting.is_system && setting.is_deletable;
  const [showSensitive, setShowSensitive] = useState(false);

  const form = useForm<SettingCreate>({
    resolver: zodResolver(settingCreateSchema),
    defaultValues: {
      setting_key: '',
      setting_value: undefined,
      default_value: undefined,
      value_type: 'string',
      allowed_values: undefined,
      category: 'general',
      name: '',
      description: '',
      seq_num: 0,
      is_system: false,
      is_sensitive: false,
      is_deletable: true,
      is_hidden_from_tenant: false,
      requires_restart: false,
    },
  });

  const watchValueType = form.watch('value_type');
  const watchIsSensitive = form.watch('is_sensitive');
  const watchAllowedValues = form.watch('allowed_values');

  useEffect(() => {
    if (open) {
      setShowSensitive(false);
      if (setting) {
        form.reset({
          setting_key: setting.setting_key,
          setting_value: setting.setting_value,
          default_value: setting.default_value,
          value_type: setting.value_type as SettingCreate['value_type'],
          allowed_values: setting.allowed_values,
          category: setting.category,
          name: setting.name,
          description: setting.description || '',
          seq_num: setting.seq_num,
          is_system: setting.is_system,
          is_sensitive: setting.is_sensitive,
          is_deletable: setting.is_deletable,
          is_hidden_from_tenant: setting.is_hidden_from_tenant,
          requires_restart: setting.requires_restart,
        });
      } else {
        form.reset({
          setting_key: '',
          setting_value: undefined,
          default_value: undefined,
          value_type: 'string',
          allowed_values: undefined,
          category: 'general',
          name: '',
          description: '',
          seq_num: 0,
          is_system: false,
          is_sensitive: false,
          is_deletable: true,
          is_hidden_from_tenant: false,
          requires_restart: false,
        });
      }
    }
  }, [open, setting, form]);

  const onSubmit = async (values: SettingCreate) => {
    await onSave(values);
    onOpenChange(false);
  };

  // Parse allowed_values for Select dropdown
  const allowedOptions: string[] = (() => {
    const av = watchAllowedValues;
    if (Array.isArray(av)) return av.map(String);
    return [];
  })();
  const hasAllowedValues = allowedOptions.length > 0;

  const renderValueInput = (fieldName: 'setting_value' | 'default_value', label: string) => (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {hasAllowedValues ? (
              <Select
                value={field.value != null ? String(field.value) : ''}
                onValueChange={field.onChange}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a value" />
                </SelectTrigger>
                <SelectContent>
                  {allowedOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : watchValueType === 'boolean' ? (
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  checked={field.value === true}
                  onCheckedChange={field.onChange}
                  disabled={isReadOnly}
                />
                <span className="text-sm text-muted-foreground">
                  {field.value === true ? 'True' : 'False'}
                </span>
              </div>
            ) : watchValueType === 'integer' ? (
              <Input
                type="number"
                value={field.value != null ? String(field.value) : ''}
                onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                disabled={isReadOnly}
              />
            ) : watchValueType === 'text' ? (
              <Textarea
                value={field.value != null ? String(field.value) : ''}
                onChange={(e) => field.onChange(e.target.value || undefined)}
                disabled={isReadOnly}
                rows={6}
              />
            ) : watchValueType === 'json' ? (
              <Textarea
                value={field.value != null ? (typeof field.value === 'string' ? field.value : JSON.stringify(field.value, null, 2)) : ''}
                onChange={(e) => {
                  try {
                    field.onChange(JSON.parse(e.target.value));
                  } catch {
                    field.onChange(e.target.value);
                  }
                }}
                disabled={isReadOnly}
                rows={4}
              />
            ) : watchIsSensitive ? (
              <div className="relative">
                <Input
                  type={showSensitive ? 'text' : 'password'}
                  value={field.value != null ? String(field.value) : ''}
                  onChange={(e) => field.onChange(e.target.value || undefined)}
                  disabled={isReadOnly}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowSensitive(!showSensitive)}
                >
                  {showSensitive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            ) : (
              <Input
                value={field.value != null ? String(field.value) : ''}
                onChange={(e) => field.onChange(e.target.value || undefined)}
                disabled={isReadOnly}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly ? 'View Setting' : isEditing ? 'Edit Setting' : 'New Setting'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="setting_key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Setting Key *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., ai.model" {...field} disabled={isReadOnly || isEditing} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., AI Model" {...field} disabled={isReadOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input placeholder="Help text for this setting" {...field} disabled={isReadOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., ai, ui, general" {...field} disabled={isReadOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="value_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Value Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={isReadOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {VALUE_TYPES.map((vt) => (
                          <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="seq_num"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} disabled={isReadOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {renderValueInput('setting_value', 'Value')}
            {renderValueInput('default_value', 'Default Value')}

            <FormField
              control={form.control}
              name="allowed_values"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Allowed Values</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='JSON array, e.g., ["option1","option2"]'
                      value={field.value != null ? (typeof field.value === 'string' ? field.value : JSON.stringify(field.value)) : ''}
                      onChange={(e) => {
                        try {
                          field.onChange(e.target.value ? JSON.parse(e.target.value) : undefined);
                        } catch {
                          field.onChange(e.target.value || undefined);
                        }
                      }}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormDescription>JSON array of allowed values for picklist</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2">
              <FormField
                control={form.control}
                name="is_system"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">System Setting</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_sensitive"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isReadOnly}
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">Sensitive</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_deletable"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isReadOnly || isEditing}
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">Deletable</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="requires_restart"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isReadOnly}
                      />
                    </FormControl>
                    <FormLabel className="font-normal cursor-pointer">Requires Cache Refresh</FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="pt-4 flex justify-between">
              <div>
                {canDelete && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      onDelete(setting!);
                      onOpenChange(false);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </Button>
                {!isReadOnly && (
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {isEditing ? 'Save Changes' : 'Create'}
                  </Button>
                )}
              </div>
            </DialogFooter>

            {isEditing && (
              <RecordMetadata record={setting} />
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
